import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { Button, type ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'

import Spinner from 'components/Spinner'
import usePaywallPremiumAcct from 'hooks/usePaywallPremiumAcct'
import useScreenSize from 'hooks/useScreenSize'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import { useSessionContext } from 'hooks/SessionContext'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'
import GA4 from 'helpers/ga4'

const PremiumWidget = () => {
  const { session: { isDealerAdmin, dealerId } } = useSessionContext()
  const { enabledFeatures, isEnabledFeaturesLoaded } = useFeaturesContext()
  const { premiumAccount, isPremiumAcctLoaded } = usePaywallPremiumAcct(dealerId)
  const screenSize = useScreenSize()
  const navigate = useNavigate()

  const title = premiumAccount && isPremiumAcctLoaded ? 'Premium Account' : 'Upgrade to Premium'
  const imageWidth = screenSize === 'small' ? '20' : '28'

  const onPaywallClick = () => {
    if (premiumAccount && isPremiumAcctLoaded) {
      const planType = premiumAccount.planId === 'diag_monthly' ? 'Monthly' : 'Yearly'

      GA4.sendEvent({
        action: 'Click Premium Settings',
        category: 'Premium Settings',
        label: `Plan Type: ${planType}`
      })

      navigate(`/paywall/details/${dealerId}`)
    } else {
      GA4.sendEvent({
        action: 'Click Upgrade to Premium',
        category: 'Premium Settings'
      })

      navigate(`/paywall/options/${dealerId}`)
    }
  }

  if (!isEnabledFeaturesLoaded || !isPremiumAcctLoaded) {
    return <S.Spinner variant="page" />
  }

  return (
    <S.Container>
      {
        (isDealerAdmin && enabledFeatures.includes('paywall')) &&
        <Button
          styles={buttonStyle}
          onPress={onPaywallClick}
        >
          <S.CrownIcon src="/img/premium_crown.png" width={imageWidth} />
          <S.PremiumButtonText>{title}</S.PremiumButtonText>
        </Button>
      }
    </S.Container>
  )
}

export default PremiumWidget

const buttonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    borderTopLeftRadius: 17,
    borderBottomLeftRadius: 17,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: '#EF4723',
    borderColor: '#EF4723'
  }
}

const S = {
  PremiumButtonText: styled.span`
    color: ${color('primaryButtonText')};
  `,
  CrownIcon: styled.img`
    margin-left: -6px;
    margin-bottom: .5px;
    margin-right: 9px;

    @media (${screenSize.small}) {
      margin-left: -8px;
    }
  }
  `,
  Container: styled.div`
    position: sticky;
    right: 0;
    margin-left: auto;
    top: 60px;
    width: max-content;
    z-index: 5000;
  `,
  Spinner: styled(Spinner)`
    margin: 10px auto;
    height: 30px;
    width: 30px;
  `
}
